import { Avatar, Badge, Button, Icon } from "@elaniintc/goes-design-system";
import { VariableKeyBox } from "./VariableKeyBox";
import { useState } from "react";
import { cn } from "@/utils/theme";
import { NodeTypes } from "@/workflow_api/types/workflow";
import { DataCenterOutputBlock } from "./configurations/data-center/components/Output";
import { InteroperabilityOutputBlock } from "./configurations/interoperability/components/Output";
import { ResolutionOutputBlock } from "./configurations/resolution/components/Output";

const nodeTypes = (node: string) => {
  switch (node) {
    case NodeTypes.FORM:
      return [
        "action",
        "bars",
        "bg-surface-action-accent-quiet border-action-quiet text-action-quiet",
        "Formulario",
      ] as const;
    case NodeTypes.IF:
      return [
        "neutral",
        "split",
        "bg-neutral-object-accent-silent border-neutral text-body",
        "Operador IF",
      ] as const;
    case NodeTypes.SWITCH:
      return [
        "neutral",
        "arrows-cross",
        "bg-neutral-object-accent-silent border-neutral text-body",
        "Operador Switch",
      ] as const;
    case NodeTypes.CALCULATED:
      return [
        "success",
        "function",
        "bg-surface-sucess-accent-quiet border-contextual-success text-success",
        "Campo Calculado",
      ] as const;
    case NodeTypes.ASSIGN_GROUP:
      return [
        "info",
        "users",
        "bg-surface-info-accent-quiet border-contextual-info text-info",
        "Asignar Grupo",
      ] as const;
    case NodeTypes.DATA_CENTER:
      return [
        "info",
        "table",
        "bg-surface-info-accent-quiet border-contextual-info-quiet text-info-quiet",
        "Centro de Datos",
      ] as const;
    case NodeTypes.CORRECTION:
      return [
        "action",
        "file-edit",
        "bg-surface-action-accent-quiet border-action-quiet text-action-quiet",
        "Revisión",
      ] as const;
    case NodeTypes.DOC_GENERATOR:
      return [
        "action",
        "file-alt",
        "bg-surface-action-accent-quiet border-contextual-action-quiet text-action-quiet",
        "Generador de Documentos",
      ] as const;
    case NodeTypes.PAYMENT:
      return [
        "warning",
        "dollar-sign",
        "bg-surface-warning-accent text-warning-quiet border-contextual-warning-quiet",
        "Pasarela de Pago",
      ] as const;
    case NodeTypes.SIGN_DOCUMENT:
      return [
        "action",
        "signature",
        "bg-surface-action-accent-quiet border-contextual-action-quiet text-action-quiet",
        "Firma Digital",
      ] as const;
    case NodeTypes.RESOLUTION:
      return [
        "action",
        "files",
        "bg-surface-action-accent-quiet border-contextual-action-quiet text-action-quiet",
        "Resolución",
      ] as const;
    case NodeTypes.INTEROPERABILITY:
      return [
        "warning",
        "diagram-nested",
        "bg-surface-warning-accent text-warning-quiet border-contextual-warning-quiet",
        "Interoperabilidad",
      ] as const;
    case NodeTypes.N8N:
      return [
        "warning",
        "hexagon-nodes",
        "bg-surface-warning-accent-quiet border-contextual-warning-quiet text-warning-quiet",
        "N8N",
      ] as const;
    case NodeTypes.EXPEDIENTE:
      return [
        "info",
        "folder-open",
        "bg-surface-info-accent-quiet border-contextual-info-quiet text-info-quiet",
        "Expediente Digital",
      ] as const;
    default:
      return [
        "neutral",
        "bars",
        "bg-surface-action-accent-quiet border-action-quiet text-action-quiet",
        "Desconocido",
      ] as const;
  }
};

interface InputOutputProps {
  nodeType?: string;
  nodeLabel?: string;
  showSteps?: boolean;
  variableName?: string;
  variableType?: string;
  isLoading?: boolean;
  params?: Record<string, string | undefined>;
  documents?: Array<{
    label: string;
    value: string;
    type: string;
    selected: boolean;
  }>; // Add selected state}>
  variables?: Array<{
    name: string;
    type: string;
  }>;
}

export const InputOutput = ({
  nodeType = "Formulario",
  nodeLabel = "",
  showSteps = true,
  variableName = "variable-key",
  variableType = "array",
  isLoading = false,
  params = {},
  documents = [],
  variables,
}: InputOutputProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [type, icon, className, label] = nodeTypes(nodeType);

  if (isLoading) {
    // loading
    return (
      <div className="min-h-[48px] text-body-loud font-semibold flex justify-center items-center py-0.5 px-3 gap-4 ">
        <Icon
          type="far"
          name="spinner-third"
          className="animate-spin text-xl text-body"
        />
      </div>
    );
  }

  return (
    <div className="p-4 pt-8">
      <div
        className={cn(
          "pl-2.5 space-x-2 flex items-center justify-start",
          isOpen ? "pb-0" : "pb-8",
        )}
      >
        <div className="bg-transparent p-1 border border-neutral-object-accent rounded-[12px]">
          <Avatar
            size="2xs"
            shape={null}
            className={`items-center justify-center border rounded-lg ${className}`}
          >
            <div className="">
              <Icon type="fas" name={icon} size="sm" className="" />
            </div>
          </Avatar>
        </div>
        <div className="flex w-full items-center space-x-2 min-w-0">
          <Badge size="sm" variant={type}>
            {label}
          </Badge>

          <div className="flex-1 font-semibold text-body-loud truncate">
            {nodeLabel}
          </div>

          <Button
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle"
            size="icon"
            variant="ghost"
            className={`ml-auto mr-3 ${isOpen ? "rotate-0" : "rotate-180"}`}
          >
            <Icon name="angle-up" type="fas" />
          </Button>
        </div>
      </div>

      {showSteps ? (
        <div
          id="content"
          className={`
        relative overflow-hidden transition-[max-height] duration-200 ease-in-out ${isOpen ? "min-h-full mb-4" : "max-h-0"}
        `}
        >
          {/* block */}
          <div className="flex ">
            <div className="w-0.5 relative last:bg-transparent bg-neutral-object-accent translate-x-[25px]">
              <span className="absolute left-3 top-[14px] w-2 h-2 bg-neutral-object-accent rounded-full"></span>
              <span className="absolute w-3 h-5 border-l-2 border-t-2 border-neutral-object-accent rotate-x-180 rounded-tl-md"></span>
            </div>

            <div className="relative pl-[50px] mb-4 w-full">
              <div className="py-1">
                <h3 className="mb-4 space-x-2">
                  <span className="text-body-quiet">Paso 1</span>
                  <span className="text-body-loud">nombre-del-paso</span>
                </h3>
                <div className="space-y-3">
                  <VariableKeyBox type="array" />
                  <VariableKeyBox type="string" />
                  <VariableKeyBox type="number" />
                </div>
              </div>
            </div>
          </div>
          {/* end block */}

          {/* block */}
          <div className="flex ">
            <div className="w-0.5 relative bg-transparent translate-x-[25px]">
              <span className="absolute left-3 top-[14px] w-2 h-2 bg-neutral-object-accent rounded-full"></span>
              <span className="absolute w-3 h-5 border-l-2 border-t-2 border-neutral-object-accent rotate-x-180 rounded-tl-md"></span>
            </div>

            <div className="relative pl-[50px] mb-4 w-full">
              <div className="py-1">
                <h3 className="mb-4 space-x-2">
                  <span className="text-body-quiet">Paso 2</span>
                  <span className="text-body-loud">nombre-del-paso</span>
                </h3>
                <div className="space-y-3">
                  <VariableKeyBox type="boolean" />
                </div>
              </div>
            </div>
          </div>
          {/* end block */}
        </div>
      ) : (
        <div
          id="content"
          className={`
        relative overflow-hidden transition-[max-height] duration-200 ease-in-out ${isOpen ? "min-h-full mb-4" : "max-h-0"}
        `}
        >
          {/* block */}
          <div className="flex ">
            <div className="w-0.5 relative last:bg-transparent translate-x-[25px]">
              <span className="absolute left-3 top-[14px] w-2 h-2 bg-neutral-object-accent rounded-full"></span>
              <span className="absolute w-3 h-5 border-l-2 border-t-2 border-neutral-object-accent rotate-x-180 rounded-tl-md"></span>
            </div>

            <div className="relative pl-[50px] mb-4 w-full">
              <div className="py-1">
                <div className="space-y-3 max-w-[420px]">
                  {nodeType === NodeTypes.DATA_CENTER ? (
                    <DataCenterOutputBlock
                      variableName={variableName}
                      variableType={variableType}
                      operation={params["operation"]}
                    />
                  ) : nodeType === NodeTypes.RESOLUTION ? (
                    <ResolutionOutputBlock />
                  ) : nodeType === NodeTypes.INTEROPERABILITY ? (
                    <InteroperabilityOutputBlock
                      serviceId={params["serviceId"]}
                      endpointId={params["endpointId"]}
                    />
                  ) : nodeType === NodeTypes.SIGN_DOCUMENT ? (
                    <>
                      <span className="text-body-quiet mb-4">
                        Documentos firmados
                      </span>

                      {documents.map((doc) => (
                        <VariableKeyBox
                          key={doc.value}
                          name={doc.label}
                          type={variableType}
                        />
                      ))}
                    </>
                  ) : variables ? (
                    <>
                      {variables.map((variable, index) => (
                        <VariableKeyBox
                          key={`${variable.name}-${index}`}
                          name={variable.name}
                          type={variable.type}
                        />
                      ))}
                    </>
                  ) : (
                    <VariableKeyBox name={variableName} type={variableType} />
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* end block */}
        </div>
      )}
    </div>
  );
};
